import React, { useEffect, useState } from "react";
import AdminSidebar from "../components/AdminSidebar";
import AdminNavbar from "../components/AdminNavbar";
import AdminAPI from "../../services/adminApi";
import { toast } from "react-toastify";

function Checklists() {
  const [checklists, setChecklists] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [selectedChecklist, setSelectedChecklist] = useState(null);

  const [filters, setFilters] = useState({
    search: "",
    date: "",
    cycle: ""
  });

  const [editForm, setEditForm] = useState({
    temperature: "",
    moisture_level: "",
    humidity: "",
    ph_level: "",
    worm_activity: "",
    odor_level: "",
    compost_color: "",
    food_consumption: "",
    food_type: "",
    food_quantity: "",
    water_quantity: "",
    bedding_condition: "",
    notes: "",
    ants_detected: 0,
    flies_detected: 0,
    maggots_detected: 0,
    fungus_detected: 0,
    leachate_present: 0,
    bed_mixed: 0,
    harvest_ready: 0
  });

  useEffect(() => {
    fetchChecklists();
    fetchAnalytics();
  }, []);

  const fetchChecklists = async () => {
    try {
      const res = await AdminAPI.get(
        `/checklists?search=${filters.search}&date=${filters.date}&cycle=${filters.cycle}`
      );
      setChecklists(res.data);
    } catch (err) {
      console.log(err);
      toast.error("Failed to load checklists");
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await AdminAPI.get(
        "/checklists/analytics/summary"
      );
      setAnalytics(res.data);
    } catch (err) {
      console.log(err);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const openChecklist = async (id) => {
    try {
      const res = await AdminAPI.get(`/checklists/${id}`);
      setSelectedChecklist(res.data);

      setEditForm({
        temperature: res.data.temperature || "",
        moisture_level: res.data.moisture_level || "",
        humidity: res.data.humidity || "",
        ph_level: res.data.ph_level || "",
        worm_activity: res.data.worm_activity || "",
        odor_level: res.data.odor_level || "",
        compost_color: res.data.compost_color || "",
        food_consumption: res.data.food_consumption || "",
        food_type: res.data.food_type || "",
        food_quantity: res.data.food_quantity || "",
        water_quantity: res.data.water_quantity || "",
        bedding_condition: res.data.bedding_condition || "",
        notes: res.data.notes || "",
        ants_detected: res.data.ants_detected || 0,
        flies_detected: res.data.flies_detected || 0,
        maggots_detected: res.data.maggots_detected || 0,
        fungus_detected: res.data.fungus_detected || 0,
        leachate_present: res.data.leachate_present || 0,
        bed_mixed: res.data.bed_mixed || 0,
        harvest_ready: res.data.harvest_ready || 0
      });

    } catch {
      toast.error("Failed to load checklist");
    }
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;

    setEditForm({
      ...editForm,
      [name]: type === "checkbox" ? (checked ? 1 : 0) : value
    });
  };

  const saveChecklist = async () => {
    try {
      await AdminAPI.put(
        `/checklists/${selectedChecklist.id}`,
        editForm
      );

      toast.success("Checklist updated");

      setSelectedChecklist(null);
      fetchChecklists();
      fetchAnalytics();

    } catch {
      toast.error("Update failed");
    }
  };

  const deleteChecklist = async (id) => {
    if (!window.confirm("Delete checklist?")) return;

    try {
      await AdminAPI.delete(`/checklists/${id}`);

      toast.success("Checklist deleted");

      fetchChecklists();
      fetchAnalytics();

    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="d-flex">
      <AdminSidebar />

      <div
        style={{
          flex: 1,
          background: "#f5f7fa",
          minHeight: "100vh",
          padding: "20px"
        }}
      >
        <AdminNavbar />

        <h2 className="mt-3">
          📋 Checklist Management
        </h2>

        {/* Analytics */}
        {analytics && (
          <div className="row mt-4 g-3">
            <div className="col-md-2">
              <div className="card shadow border-0">
                <div className="card-body">
                  <h6>Total</h6>
                  <h3>{analytics.total_checklists}</h3>
                </div>
              </div>
            </div>

            <div className="col-md-2">
              <div className="card shadow border-0">
                <div className="card-body">
                  <h6>Avg Temp</h6>
                  <h3>{analytics.avg_temperature}</h3>
                </div>
              </div>
            </div>

            <div className="col-md-2">
              <div className="card shadow border-0">
                <div className="card-body">
                  <h6>Avg Moisture</h6>
                  <h3>{analytics.avg_moisture}</h3>
                </div>
              </div>
            </div>

            <div className="col-md-2">
              <div className="card shadow border-0">
                <div className="card-body">
                  <h6>Fungus</h6>
                  <h3>{analytics.fungus_cases}</h3>
                </div>
              </div>
            </div>

            <div className="col-md-2">
              <div className="card shadow border-0">
                <div className="card-body">
                  <h6>Harvest Ready</h6>
                  <h3>{analytics.harvest_ready_count}</h3>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="card shadow border-0 mt-4">
          <div className="card-body">
            <div className="d-flex gap-3 flex-wrap">
              <input
                type="text"
                name="search"
                className="form-control"
                style={{ width: "250px" }}
                placeholder="Search farmer"
                value={filters.search}
                onChange={handleFilterChange}
              />

              <input
                type="date"
                name="date"
                className="form-control"
                style={{ width: "220px" }}
                value={filters.date}
                onChange={handleFilterChange}
              />

              <input
                type="number"
                name="cycle"
                className="form-control"
                style={{ width: "180px" }}
                placeholder="Cycle ID"
                value={filters.cycle}
                onChange={handleFilterChange}
              />

              <button
                className="btn btn-success"
                onClick={fetchChecklists}
              >
                Search
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card shadow border-0 mt-4">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-bordered table-hover">
                <thead className="table-success">
                  <tr>
                    <th>Farmer</th>
                    <th>Date</th>
                    <th>Temp</th>
                    <th>Moisture</th>
                    <th>Humidity</th>
                    <th>pH</th>
                    <th>Harvest</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {checklists.map((item) => (
                    <tr key={item.id}>
                      <td>{item.full_name}</td>
                      <td>{item.checklist_date}</td>
                      <td>{item.temperature}</td>
                      <td>{item.moisture_level}</td>
                      <td>{item.humidity}</td>
                      <td>{item.ph_level}</td>
                      <td>
                        {item.harvest_ready ? "Yes" : "No"}
                      </td>

                      <td>
                        <button
                          className="btn btn-primary btn-sm me-2"
                          onClick={() => openChecklist(item.id)}
                        >
                          View/Edit
                        </button>

                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() =>
                            deleteChecklist(item.id)
                          }
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>

              </table>
            </div>
          </div>
        </div>

        {/* Modal */}
        {selectedChecklist && (
          <div
            className="modal d-block"
            style={{
              background: "rgba(0,0,0,0.6)"
            }}
          >
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5>
                    Checklist — {selectedChecklist.full_name}
                  </h5>

                  <button
                    className="btn-close"
                    onClick={() =>
                      setSelectedChecklist(null)
                    }
                  />
                </div>

                <div className="modal-body">
                  <div className="row g-3">
                    {[
                      ["temperature", "Temperature"],
                      ["moisture_level", "Moisture"],
                      ["humidity", "Humidity"],
                      ["ph_level", "pH"],
                      ["worm_activity", "Worm Activity"],
                      ["odor_level", "Odor Level"],
                      ["compost_color", "Compost Color"],
                      ["food_consumption", "Food Consumption"],
                      ["food_type", "Food Type"],
                      ["food_quantity", "Food Quantity"],
                      ["water_quantity", "Water Quantity"],
                      ["bedding_condition", "Bedding Condition"]
                    ].map(([key, label]) => (
                      <div className="col-md-4" key={key}>
                        <label>{label}</label>
                        <input
                          name={key}
                          className="form-control"
                          value={editForm[key]}
                          onChange={handleEditChange}
                        />
                      </div>
                    ))}

                    <div className="col-12">
                      <label>Notes</label>
                      <textarea
                        name="notes"
                        className="form-control"
                        rows="3"
                        value={editForm.notes}
                        onChange={handleEditChange}
                      />
                    </div>

                    {[
                      "ants_detected",
                      "flies_detected",
                      "maggots_detected",
                      "fungus_detected",
                      "leachate_present",
                      "bed_mixed",
                      "harvest_ready"
                    ].map((key) => (
                      <div
                        className="col-md-3"
                        key={key}
                      >
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            name={key}
                            checked={editForm[key] === 1}
                            onChange={handleEditChange}
                          />
                          <label className="form-check-label">
                            {key}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() =>
                      setSelectedChecklist(null)
                    }
                  >
                    Close
                  </button>

                  <button
                    className="btn btn-success"
                    onClick={saveChecklist}
                  >
                    Save Changes
                  </button>
                </div>

              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default Checklists;